import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import StatusBadge from "@/components/StatusBadge";
import { Eye, Copy } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => v != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v) : "$0.00";
const formatDateTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "\u2014";

export default function Payments() {
  const { token } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [filter, setFilter] = useState("received");

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filter === "received") params.paid_only = true;
      if (filter === "duplicate") params.duplicate_only = true;
      if (filter === "failed") params.status = "invoice_failed";
      const res = await axios.get(`${API}/payments`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(res.data.payments || []);
    } catch (err) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const copyPayload = (payload) => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success("Payload copied");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="payments-title">Payments Received</h1>
        <p className="text-sm text-muted-foreground mt-1">Confirmed incoming dealer payments</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex gap-2">
            {[{key: "received", label: "Received"}, {key: "all", label: "All Events"}, {key: "duplicate", label: "Duplicates"}, {key: "failed", label: "Failed"}].map((f) => (
              <Button
                key={f.key}
                variant={filter === f.key ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f.key)}
                data-testid={`payments-filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Dealer</TableHead>
                <TableHead className="text-right">Gross Amount</TableHead>
                <TableHead className="text-right">Invoice Amount</TableHead>
                <TableHead>Transaction ID</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                {filter !== "received" && <TableHead>Duplicate</TableHead>}
                <TableHead>Payload</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1,2,3,4,5].map((key) => (
                  <TableRow key={`skeleton-${key}`}><TableCell colSpan={filter !== "received" ? 9 : 8}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : payments.length === 0 ? (
                <TableRow><TableCell colSpan={filter !== "received" ? 9 : 8} className="text-center py-8 text-muted-foreground">
                  {filter === "received" ? "No payments received yet" : "No events found"}
                </TableCell></TableRow>
              ) : (
                payments.map((p) => (
                  <TableRow key={p.id} className="table-row-hover" data-testid={`payment-row-${p.id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(p.platform_received_at)}</TableCell>
                    <TableCell className="font-medium text-sm">{p.dealer_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(p.gross_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(p.invoice_amount)}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[200px] truncate">{p.external_transaction_id}</TableCell>
                    <TableCell className="text-sm">{p.source}</TableCell>
                    <TableCell><StatusBadge status={p.processing_status} /></TableCell>
                    {filter !== "received" && (
                      <TableCell>{p.is_duplicate ? <StatusBadge status="duplicate" /> : <span className="text-xs text-muted-foreground">\u2014</span>}</TableCell>
                    )}
                    <TableCell>
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => setSelectedPayload(p.raw_payload_json)}
                        data-testid={`view-payload-${p.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selectedPayload} onOpenChange={() => setSelectedPayload(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Raw Webhook Payload
              <Button variant="outline" size="sm" onClick={() => copyPayload(selectedPayload)} data-testid="copy-payload">
                <Copy className="w-3 h-3 mr-2" /> Copy
              </Button>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <pre className="font-mono text-xs bg-muted rounded-lg p-4 whitespace-pre-wrap break-all">
              {JSON.stringify(selectedPayload, null, 2)}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
